/**
 * Canonical call dispositions, persisted to `interactions.outcome_category`.
 * Named for outbound, where they started; voice calls in BOTH directions write
 * them (ADR-259). The voicemail/no-answer/unavailable states come from
 * outbound's AMD gate and reachability; inbound mostly produces the
 * reached/handoff values plus `failed` (a session that crashed mid-call).
 *
 * ONE mutually-exclusive disposition per call — the *result* of a call
 * that reached a terminal state. This is orthogonal to `interactions.status`
 * (the lifecycle: pending → in_progress → completed/failed), which answers
 * "where in the pipeline" rather than "how it resolved".
 *
 * The column lives on the unified `interactions` table, so the SAME column
 * is written by every channel (voice today; the SMS scheduling agent and
 * any future channel later). To keep channels from colliding semantically
 * the values are grouped: {@link REACHED_OUTCOMES} + `wrong_number` are
 * universal, the telephony states are voice-only, and a small SMS-only set
 * is reserved for the follow-on that wires the SMS agent to write outcomes.
 *
 * Metrics queries always scope to a single channel/direction (voice call
 * tiles use `channel='voice' AND direction='outbound'`), so a value written
 * by one channel can never pollute another channel's tiles.
 */
export const OUTBOUND_CALL_OUTCOME = {
  // ─── Reached the patient (universal across channels) ───
  /** Booked an appointment on this call. */
  SCHEDULED: 'scheduled',
  /** Spoke with the patient but nothing booked and no other outcome applies. */
  ENGAGED_NO_BOOKING: 'engaged_no_booking',
  /** Patient explicitly declined to schedule. */
  DECLINED: 'declined',
  /** Patient asked us to call back later. */
  CALLBACK_REQUESTED: 'callback_requested',
  /** Reached the wrong number / the patient doesn't live there. */
  WRONG_NUMBER: 'wrong_number',

  // ─── Didn't reach the patient (voice-only telephony states) ───
  /** Left (or attempted) a voicemail. */
  VOICEMAIL: 'voicemail',
  /** Rang out — nobody answered, no voicemail reached. */
  NO_ANSWER: 'no_answer',
  /** Number disconnected / unavailable / mailbox unreachable. */
  UNAVAILABLE: 'unavailable',
  /** Technical / SIP failure — distinct from a patient no-answer. */
  FAILED: 'failed',

  // ─── Handoff (universal) ───
  /** Transferred / escalated to a human staff member. */
  ESCALATED: 'escalated',
} as const;

export type OutboundCallOutcome =
  (typeof OUTBOUND_CALL_OUTCOME)[keyof typeof OUTBOUND_CALL_OUTCOME];

/**
 * All disposition values as a tuple — for `z.enum(...)` validation at the
 * internal write boundary. Cast is sound: `Object.values` of a `const`
 * object yields exactly the union members.
 */
export const OUTBOUND_CALL_OUTCOME_VALUES = Object.values(OUTBOUND_CALL_OUTCOME) as [
  OutboundCallOutcome,
  ...OutboundCallOutcome[],
];

/**
 * Reserved SMS-only dispositions. Not written by the voice path; declared
 * here so the shared vocabulary is complete when the SMS agent adopts it.
 */
export const SMS_CALL_OUTCOME = {
  /** No reply within the follow-up window. */
  NO_RESPONSE: 'no_response',
  /** Patient opted out of messaging (STOP). */
  OPTED_OUT: 'opted_out',
} as const;

export type SmsCallOutcome = (typeof SMS_CALL_OUTCOME)[keyof typeof SMS_CALL_OUTCOME];

/**
 * Dispositions that mean "we reached the patient" — the numerator for the
 * connect rate and the denominator for the booking rate. Derived in the
 * dashboard fetchers; never stored.
 */
export const REACHED_OUTCOMES: readonly OutboundCallOutcome[] = [
  OUTBOUND_CALL_OUTCOME.SCHEDULED,
  OUTBOUND_CALL_OUTCOME.ENGAGED_NO_BOOKING,
  OUTBOUND_CALL_OUTCOME.DECLINED,
  OUTBOUND_CALL_OUTCOME.CALLBACK_REQUESTED,
];

/** True when the disposition means the patient was reached. */
export function isReachedOutcome(outcome: string | null | undefined): boolean {
  return !!outcome && (REACHED_OUTCOMES as readonly string[]).includes(outcome);
}

/**
 * Display order + human labels for the disposition-breakdown tiles.
 * Ordered reached → not-reached → system so the composition bar reads
 * top-to-bottom the way an operator scans it.
 */
export const OUTBOUND_CALL_OUTCOME_LABELS: ReadonlyArray<{
  value: OutboundCallOutcome;
  label: string;
}> = [
  { value: OUTBOUND_CALL_OUTCOME.SCHEDULED, label: 'Scheduled' },
  { value: OUTBOUND_CALL_OUTCOME.ENGAGED_NO_BOOKING, label: 'Spoke, no action' },
  { value: OUTBOUND_CALL_OUTCOME.DECLINED, label: 'Declined' },
  { value: OUTBOUND_CALL_OUTCOME.CALLBACK_REQUESTED, label: 'Callback requested' },
  { value: OUTBOUND_CALL_OUTCOME.VOICEMAIL, label: 'Voicemail' },
  { value: OUTBOUND_CALL_OUTCOME.NO_ANSWER, label: 'Not answered' },
  { value: OUTBOUND_CALL_OUTCOME.UNAVAILABLE, label: 'Disconnected / unavailable' },
  { value: OUTBOUND_CALL_OUTCOME.WRONG_NUMBER, label: 'Wrong number' },
  { value: OUTBOUND_CALL_OUTCOME.FAILED, label: 'Failed' },
  // NOT "Transferred to staff", which the label said and which is false for the
  // case that actually occurs. The disposition is recorded whether or not the
  // bridge took — a caller handed to the front desk and one promised a call
  // back BY it are the same to a campaign, and that is what stops a redial
  // (ADR-198) — but a practice with no `transfer_phone` configured is every
  // practice by default, so today every row under this value is a call where
  // nobody was transferred. An operator reading a column of "Transferred"
  // concludes the feature works when not one call has been bridged. "Escalated"
  // is true on both paths: the request left us either way. WHICH of the two
  // happened is `humanHandoffOutcome`, which is where a configuration gap is
  // told apart from a telephony fault.
  { value: OUTBOUND_CALL_OUTCOME.ESCALATED, label: 'Escalated to staff' },
];
